import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Eye, EyeOff, Save, X, Search,
  FileText, Calendar, Tag, Image as ImageIcon, RefreshCw, ExternalLink
} from 'lucide-react';
import ImageUploader from '@/components/ImageUploader';
import RichTextEditor from '@/components/RichTextEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Health & Wellness', 'Buying Guide', 'Technology Comparison',
  'Troubleshooting & Maintenance', 'Maintenance', 'Water Quality',
  'Tips & Tricks', 'News & Updates'
];

function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchBlogs(); }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/blogs?published=false`);
      if (res.ok) setBlogs(await res.json());
    } catch { toast.error('Failed to load blogs'); }
    finally { setLoading(false); }
  };

  const saveBlog = async (data) => {
    setSaving(true);
    try {
      const isNew = !data.id;
      const url = isNew ? `${BACKEND_URL}/api/blogs` : `${BACKEND_URL}/api/blogs/${data.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        toast.success(isNew ? 'Blog created!' : 'Blog updated!');
        fetchBlogs();
        setEditing(null);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } catch { toast.error('Save error'); }
    finally { setSaving(false); }
  };

  const deleteBlog = async (id) => {
    if (!confirm('Delete this blog post permanently?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/blogs/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Blog deleted'); fetchBlogs(); }
    } catch { toast.error('Delete failed'); }
  };

  const newBlog = () => setEditing({
    title: '', excerpt: '', content: '', featuredImage: '',
    category: 'Health & Wellness', tags: [], published: true,
    metaTitle: '', metaDescription: '', metaKeywords: []
  });

  const filtered = blogs.filter(b =>
    b.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen" data-testid="admin-blogs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="blogs-page-title">Blog Management</h1>
          <p className="text-gray-500 text-sm">Create and manage blog posts</p>
        </div>
        <button onClick={newBlog} data-testid="add-blog-btn"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
          <Plus size={16} /> New Blog Post
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm mb-4 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input type="text" placeholder="Search blogs..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="blog-search" />
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl p-12 flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center" data-testid="blogs-empty">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-medium text-gray-900 mb-1">No blog posts yet</h3>
            <p className="text-gray-500 text-sm mb-4">Create your first blog post to get started</p>
            <button onClick={newBlog} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
              <Plus size={16} className="inline mr-1" /> Create Blog Post
            </button>
          </div>
        ) : (
          filtered.map(blog => (
            <div key={blog.id} className={`bg-white rounded-xl shadow-sm overflow-hidden ${!blog.published ? 'opacity-60' : ''}`}
              data-testid={`blog-item-${blog.id}`}>
              <div className="flex items-center gap-4 p-4">
                {blog.featuredImage && (
                  <img src={blog.featuredImage} alt="" className="w-20 h-14 object-cover rounded-lg border flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[11px] font-semibold rounded">{blog.category}</span>
                    {!blog.published && <span className="px-2 py-0.5 bg-gray-200 text-gray-500 text-[11px] rounded">Draft</span>}
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm truncate">{blog.title}</h3>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{blog.excerpt}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-xs text-gray-400 mr-2">{blog.views || 0} views</span>
                  <button onClick={() => setEditing({ ...blog })}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium flex items-center gap-1.5"
                    data-testid={`edit-blog-${blog.id}`}>
                    <Edit size={14} /> Edit
                  </button>
                  <button onClick={() => deleteBlog(blog.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                    data-testid={`delete-blog-${blog.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {editing && (
        <BlogEditor blog={editing} onSave={saveBlog} onClose={() => setEditing(null)} saving={saving} />
      )}
    </div>
  );
}

function BlogEditor({ blog, onSave, onClose, saving }) {
  const [fd, setFd] = useState({ ...blog });
  const [tagInput, setTagInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');

  const set = (field, value) => setFd(prev => ({ ...prev, [field]: value }));

  const addTag = () => {
    if (tagInput.trim() && !(fd.tags || []).includes(tagInput.trim())) {
      set('tags', [...(fd.tags || []), tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (idx) => set('tags', (fd.tags || []).filter((_, i) => i !== idx));

  const addKeyword = () => {
    if (keywordInput.trim() && !(fd.metaKeywords || []).includes(keywordInput.trim())) {
      set('metaKeywords', [...(fd.metaKeywords || []), keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (idx) => set('metaKeywords', (fd.metaKeywords || []).filter((_, i) => i !== idx));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fd.title?.trim()) { toast.error('Title is required'); return; }
    if (!fd.excerpt?.trim()) { toast.error('Excerpt is required'); return; }
    if (!fd.content?.trim()) { toast.error('Content is required'); return; }
    if (!fd.featuredImage?.trim()) { toast.error('Featured image is required'); return; }
    onSave(fd);
  };

  const inputCls = "w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="blog-editor-modal">
      <div className="bg-white rounded-xl my-6 shadow-2xl max-w-4xl w-full">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10 rounded-t-xl">
          <span className="text-sm font-semibold text-gray-800">{fd.id ? 'Edit Blog Post' : 'New Blog Post'}</span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm" data-testid="blog-editor-cancel">Cancel</button>
            <button onClick={handleSubmit} disabled={saving} data-testid="blog-editor-save"
              className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium flex items-center gap-1.5">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 56px)' }}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Title *</label>
            <input type="text" value={fd.title || ''} onChange={e => set('title', e.target.value)}
              className={inputCls} placeholder="Blog post title" data-testid="blog-title" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Category *</label>
              <select value={fd.category || ''} onChange={e => set('category', e.target.value)} className={inputCls} data-testid="blog-category">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Author</label>
              <input type="text" value={fd.author || 'RO Dekho Team'} onChange={e => set('author', e.target.value)}
                className={inputCls} data-testid="blog-author" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Excerpt *</label>
            <textarea value={fd.excerpt || ''} onChange={e => set('excerpt', e.target.value)}
              className={`${inputCls} resize-y`} rows={2} placeholder="Short summary for listing page" data-testid="blog-excerpt" />
          </div>

          <ImageUploader value={fd.featuredImage || ''} onChange={url => set('featuredImage', url)} folder="cms" type="hero" label="Featured Image *" />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Content *</label>
            <RichTextEditor value={fd.content || ''} onChange={val => set('content', val)} placeholder="Write your blog content here..." minHeight="300px" />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Tags</label>
            <div className="flex gap-2 mb-2">
              <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className={inputCls} placeholder="Add tag and press Enter" data-testid="blog-tag-input" />
              <button type="button" onClick={addTag} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">Add</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(fd.tags || []).map((tag, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                  {tag}
                  <button type="button" onClick={() => removeTag(i)} className="hover:text-red-500"><X size={12} /></button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t">
            <input type="checkbox" id="blog_published" checked={fd.published !== false} onChange={e => set('published', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded" data-testid="blog-published" />
            <label htmlFor="blog_published" className="text-sm text-gray-600">Published (visible on website)</label>
          </div>

          {/* SEO Section */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
              <Search size={14} className="text-green-600" /> SEO Settings
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Meta Title</label>
                <input type="text" value={fd.metaTitle || ''} onChange={e => set('metaTitle', e.target.value)}
                  className={inputCls} placeholder="SEO title for search engines" data-testid="blog-meta-title" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Meta Description</label>
                <textarea value={fd.metaDescription || ''} onChange={e => set('metaDescription', e.target.value)}
                  className={`${inputCls} resize-y`} rows={2} placeholder="SEO description" data-testid="blog-meta-desc" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Meta Keywords</label>
                <div className="flex gap-2 mb-2">
                  <input type="text" value={keywordInput} onChange={e => setKeywordInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                    className={inputCls} placeholder="Add keyword and press Enter" data-testid="blog-keyword-input" />
                  <button type="button" onClick={addKeyword} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">Add</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(fd.metaKeywords || []).map((kw, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                      {kw}
                      <button type="button" onClick={() => removeKeyword(i)} className="hover:text-red-500"><X size={12} /></button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminBlogs;
